import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bomberosCapacitacionAPI } from '../../lib/api';
import { BomberosHeader } from '../../components/BomberosHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Upload,
    Download,
    FileSpreadsheet,
    CheckCircle,
    XCircle,
    Loader2,
} from 'lucide-react';
import * as XLSX from 'xlsx';

const PLANTILLA_COLUMNAS = [
    { key: 'nombre', label: 'Nombre del Curso', required: true, example: 'Incendio Estructural Nivel Inicial' },
    { key: 'codigo', label: 'Codigo', required: false, example: 'CAP-2025-001' },
    { key: 'nivel_anb', label: 'Nivel (postulante/inicial/operativo/profesional)', required: false, example: 'inicial' },
    { key: 'malla_curricular', label: 'Malla Curricular (2024, 2025, o ambos)', required: false, example: '2025' },
    { key: 'horas_duracion', label: 'Horas Duracion', required: false, example: '16' },
    { key: 'fecha_inicio', label: 'Fecha Inicio (YYYY-MM-DD)', required: false, example: '2025-03-01' },
    { key: 'fecha_fin', label: 'Fecha Fin (YYYY-MM-DD)', required: false, example: '2025-03-15' },
    { key: 'instructor', label: 'Instructor', required: false, example: 'Cap. Juan Perez' },
    { key: 'lugar', label: 'Lugar', required: false, example: 'Cuartel Central' },
    { key: 'cupo_maximo', label: 'Cupo Maximo', required: false, example: '20' },
    { key: 'es_aula_virtual', label: 'Aula Virtual (Si/No)', required: false, example: 'No' },
    { key: 'es_complementario', label: 'Complementario (Si/No)', required: false, example: 'No' },
    { key: 'descripcion', label: 'Descripcion', required: false, example: 'Curso teorico-practico de combate de incendios' },
];

export default function ImportarCapacitacionesPage() {
    const navigate = useNavigate();
    const [registros, setRegistros] = useState([]);
    const [resultado, setResultado] = useState(null);
    const [importing, setImporting] = useState(false);
    const [fileName, setFileName] = useState('');

    const handleDescargarPlantilla = () => {
        const headers = PLANTILLA_COLUMNAS.map(c => c.label);
        const examples = PLANTILLA_COLUMNAS.map(c => c.example);
        
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([headers, examples]);
        
        // Set column widths
        ws['!cols'] = PLANTILLA_COLUMNAS.map(c => ({ wch: Math.max(c.label.length, c.example.length) + 2 }));
        
        XLSX.utils.book_append_sheet(wb, ws, 'Capacitaciones');
        XLSX.writeFile(wb, 'plantilla_capacitaciones.xlsx');
        toast.success('Plantilla descargada');
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        setFileName(file.name);
        setResultado(null);

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                
                // Map header labels to keys
                const labelToKey = {};
                PLANTILLA_COLUMNAS.forEach(c => { labelToKey[c.label] = c.key; });
                
                const mapped = jsonData.map(row => {
                    const mapped_row = {};
                    Object.entries(row).forEach(([header, value]) => {
                        const key = labelToKey[header] || header;
                        mapped_row[key] = value;
                    });
                    return mapped_row;
                });

                setRegistros(mapped);
                toast.success(`${mapped.length} registros cargados desde ${file.name}`);
            } catch (err) {
                toast.error('Error al leer archivo');
                console.error(err);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleImportar = async () => {
        if (registros.length === 0) {
            toast.error('No hay registros para importar');
            return;
        }

        try {
            setImporting(true);
            const res = await bomberosCapacitacionAPI.importar(registros);
            setResultado(res.data);
            toast.success(res.data.message);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al importar');
        } finally {
            setImporting(false);
        }
    };

    return (
        <>
        <BomberosHeader />
        <div className="space-y-4 md:space-y-6 p-4 md:p-6 pt-20 md:pt-20 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/capacitaciones')} data-testid="back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-lg sm:text-xl md:text-2xl font-bold flex items-center gap-2">
                        <FileSpreadsheet className="h-5 w-5 md:h-6 md:w-6 shrink-0" />
                        Importar Capacitaciones
                    </h1>
                    <p className="text-xs sm:text-sm text-muted-foreground">Carga masiva desde plantilla Excel</p>
                </div>
            </div>

            {/* Paso 1: Descargar plantilla */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Badge className="bg-blue-600 text-white">1</Badge>
                        Descargar Plantilla
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-muted-foreground mb-3">
                        Descargue la plantilla Excel, complete los datos y vuelva a subirla. El campo "Nombre del Curso" es obligatorio.
                        Para malla curricular, escriba "2024", "2025", o "2024, 2025" para ambas.
                    </p>
                    <Button variant="outline" onClick={handleDescargarPlantilla} data-testid="descargar-plantilla-btn">
                        <Download className="h-4 w-4 mr-2" />
                        Descargar Plantilla Excel
                    </Button>
                </CardContent>
            </Card>

            {/* Paso 2: Subir archivo */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Badge className="bg-blue-600 text-white">2</Badge>
                        Subir Archivo
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="border-2 border-dashed rounded-lg p-6 text-center">
                        <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                        <input
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="file-upload"
                            data-testid="file-upload-input"
                        />
                        <label htmlFor="file-upload" className="cursor-pointer">
                            <p className="font-medium text-sm">
                                {fileName ? fileName : 'Haga clic para seleccionar archivo'}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">Formatos: .xlsx, .xls, .csv</p>
                        </label>
                    </div>

                    {registros.length > 0 && (
                        <div className="mt-4">
                            <p className="text-sm font-medium mb-2">{registros.length} registros listos para importar:</p>
                            <div className="max-h-48 overflow-y-auto border rounded-md">
                                <table className="w-full text-xs">
                                    <thead className="bg-muted sticky top-0">
                                        <tr>
                                            <th className="p-2 text-left">#</th>
                                            <th className="p-2 text-left">Nombre</th>
                                            <th className="p-2 text-left">Nivel</th>
                                            <th className="p-2 text-left">Malla</th>
                                            <th className="p-2 text-left">Horas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {registros.map((r, i) => (
                                            <tr key={i} className="border-t">
                                                <td className="p-2">{i + 1}</td>
                                                <td className="p-2 font-medium">{r.nombre || '-'}</td>
                                                <td className="p-2 capitalize">{r.nivel_anb || 'inicial'}</td>
                                                <td className="p-2">{r.malla_curricular || '-'}</td>
                                                <td className="p-2">{r.horas_duracion || 8}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Paso 3: Importar */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Badge className="bg-blue-600 text-white">3</Badge>
                        Importar
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Button
                        onClick={handleImportar}
                        disabled={registros.length === 0 || importing}
                        data-testid="importar-btn"
                    >
                        {importing ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importando...</>
                        ) : (
                            <><Upload className="h-4 w-4 mr-2" /> Importar {registros.length} Capacitaciones</>
                        )}
                    </Button>

                    {resultado && (
                        <div className="mt-4 space-y-2">
                            <div className="flex gap-3">
                                <Badge className="bg-green-100 text-green-700">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    {resultado.creados} creados
                                </Badge>
                                <Badge className="bg-blue-100 text-blue-700">
                                    {resultado.actualizados} actualizados
                                </Badge>
                                {resultado.errores?.length > 0 && (
                                    <Badge className="bg-red-100 text-red-700">
                                        <XCircle className="h-3 w-3 mr-1" />
                                        {resultado.errores.length} errores
                                    </Badge>
                                )}
                            </div>
                            {resultado.errores?.length > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs max-h-32 overflow-y-auto">
                                    {resultado.errores.map((err, i) => (
                                        <p key={i} className="text-red-700">{err}</p>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
        </>
    );
}
